/** LoRaWAN point definitions - matches backend structure */
import {DeviceId, DeviceType} from './device';
import {TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL, Tile} from './tile';
import {TileIcon} from './tile-icon';
import {TileStructure} from './structure';
import {
  TILE_APPEARANCE_DEFAULT_DECIMAL,
  TILE_APPEARANCE_DEFAULT_INCREMENT,
  TILE_APPEARANCE_DEFAULT_MULTIPLIER,
} from './appearance';

/** Data source: payload (UP) or metadata (UM) */
export enum LoRaWANPointType {
  UplinkPayload = 'UP',
  UplinkMetadata = 'UM'
}

/** Point data type */
export enum LoRaWANPointFormat {
  Numeric = 'num',
  Boolean = 'bool'
}

/** LoRaWAN point. PointID format: ["UP", "temperature", "num"] */
export interface LoRaWANPoint {
  Name: string;
  Type: LoRaWANPointType;
  Format: LoRaWANPointFormat;
}

/**
 * A point as stored in the device config — matches backend lorawan.Point.
 * ID is a tuple of [type, name, format] that serialises as a JSON array.
 */
export interface LoRaWANDevicePoint {
  ID: [LoRaWANPointType, string, LoRaWANPointFormat];
}

/** LoRaWAN device configuration — matches backend lorawan.LoRaWANDeviceConfig */
export interface LoRaWANConfig {
  ApplicationID: string;
  DevEUI: string;
  JoinEUI: string;
  DevAddr: string;
  Points: LoRaWANDevicePoint[];
}

/** Creates LoRaWAN PointID as JSON: ["UP", "temperature", "num"] */
export function makeLoRaWANPointID(type: LoRaWANPointType, name: string, format: LoRaWANPointFormat): string {
  return JSON.stringify([type, name, format]);
}

/** Converts a LoRaWANDevicePoint to the string PointID used in tile structures */
export function lorawanDevicePointToPointID(point: LoRaWANDevicePoint): string {
  return JSON.stringify(point.ID);
}

/** Creates a Tile from a LoRaWANDevicePoint, mirroring makeTileFromModbusPoint */
export function makeTileFromLoRaWANPoint(
  point: LoRaWANDevicePoint,
  deviceId: DeviceId,
  icon: TileIcon = 'dot-circle',
): Tile {
  const tile = new Tile();
  tile.Name = point.ID[1];
  tile.Appearance = {
    Icon: icon,
    Color: '',
    Config: {
      Decimal: TILE_APPEARANCE_DEFAULT_DECIMAL,
      Increment: TILE_APPEARANCE_DEFAULT_INCREMENT,
      Multiplier: TILE_APPEARANCE_DEFAULT_MULTIPLIER,
    },
  };

  const pointID = lorawanDevicePointToPointID(point);

  if (point.ID[2] === LoRaWANPointFormat.Boolean) {
    tile.Type = TILE_TYPE_SIMPLE_BOOL;
    const s: TileStructure = {
      Priority: 0,
      Protocol: DeviceType.LoRaWAN,
      DeviceID: deviceId,
      PointID: pointID,
      DisplayFalse: '',
      DisplayTrue: '',
      Writable: false,
      Conditions: [],
      AutoClear: '',
    };
    tile.Structure = s;
  } else {
    tile.Type = TILE_TYPE_SIMPLE_ANALOG;
    const s: TileStructure = {
      Priority: 0,
      Protocol: DeviceType.LoRaWAN,
      DeviceID: deviceId,
      PointID: pointID,
      DisplayRawUnits: '',
      DisplayStates: [],
      Writable: false,
      Conditions: [],
      AutoClear: '',
    };
    tile.Structure = s;
  }

  tile.setDefaults();
  return tile;
}
